export type MeResponse =
  | { authenticated: false }
  | { authenticated: true; username: string; name: string; email: string; role: string; allowed: boolean; dbId: number }

export interface Category {
  id: number
  key: string
  name: string
  description: string | null
  addressList: string
  sortOrder: number
  createdAt: string
  updatedAt: string
  _count?: { domains: number }
}

export interface IpGroup {
  id: number
  key: string
  name: string
  description: string | null
  addressList: string
  sortOrder: number
  _count?: { ipRanges: number }
}

export interface HappRuleSet {
  id: number
  key: string
  name: string
  description: string | null
  sortOrder: number
  tags: HappTag[]
  _count?: { tags: number }
}

export interface Domain {
  id: number
  name: string
  categoryId: number
  notes: string | null
  enabled: boolean
  matchSubdomain: boolean
  createdAt: string
  createdBy: string | null
  category: { key: string; name: string }
}

export interface IpRange {
  id: number
  address: string
  groupId: number
  comment: string | null
  enabled: boolean
  createdAt: string
  createdBy: string | null
  group?: { key: string; name: string }
}

export interface HappTag {
  id: number
  ruleSetId: number
  tagType: 'geosite' | 'geoip'
  value: string
}

export interface Target {
  id: number
  slug: string
  name: string
  type: 'mikrotik' | 'happ' | 'plain' | 'remnawave'
  token: string
  description: string | null
  enabled: boolean
  configJson: string | null
  lastFetchAt: string | null
  fetchCount: number
  _count?: { extraDomains: number; extraIpRanges: number }
  categories: Array<{ categoryId: number; enabled: boolean }>
  ipGroups: Array<{ ipGroupId: number; enabled: boolean }>
  happRuleSets: Array<{ ruleSetId: number; enabled: boolean }>
}

export interface DomainsResponse {
  items: Domain[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface Panel {
  id: number
  slug: string
  name: string
  type: 'remnawave'
  url: string
  artifact: string
  enabled: boolean
  autoSync: boolean
  syncIntervalSec: number
  targetId: number
  authDataMask: string
  hasAuth: boolean
  lastSyncAt: string | null
  lastSyncOk: boolean | null
  lastSyncError: string | null
  syncCount: number
  target?: { slug: string; name: string; type: string }
}

export interface ApiToken {
  id: number
  name: string
  prefix: string
  role: 'viewer' | 'editor' | 'admin'
  enabled: boolean
  createdBy: string | null
  createdAt: string
  lastUsedAt: string | null
  useCount: number
}

export interface AuditEntry {
  id: number
  userId: number | null
  username: string | null
  action: string
  entityType: string
  entityId: number | null
  payload: string | null
  createdAt: string
}

class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

// Локализация серверных ошибок
const errorTranslations: Record<string, string> = {
  'unauthorized': 'Требуется авторизация',
  'forbidden': 'Доступ запрещён',
  'not found': 'Не найдено',
  'invalid key': 'Неверный ключ (только латиница, цифры, подчёркивание)',
  'invalid address': 'Неверный IP или CIDR',
  'bad tagType': 'Неверный тип тега',
  'already exists': 'Уже существует',
  'exists': 'Уже существует',
  'slug exists': 'Slug уже занят',
  'domain exists': 'Домен уже существует',
  'name required': 'Укажите имя',
  'slug, name, type required': 'Slug, имя и тип обязательны',
  'yaml required': 'YAML обязателен',
  'invalid yaml': 'Неверный YAML',
  'insufficient role': 'Недостаточно прав',
  'invalid token': 'Неверный API-токен',
  'token disabled': 'API-токен отключён',
  'invalid role': 'Неверная роль',
  'invalid domain format': 'Неверный формат домена',
  'category not found': 'Категория не найдена',
  'group not found': 'Группа IP не найдена',
  'api tokens cannot manage api tokens': 'Управление токенами доступно только из UI'
}

function translateError(msg: string): string {
  const lower = msg.toLowerCase().trim()
  for (const [en, ru] of Object.entries(errorTranslations)) {
    if (lower === en || lower.startsWith(en + ':')) return ru
  }
  return msg
}

async function req<T>(method: string, path: string, body?: any): Promise<T> {
  const opts: RequestInit = { method, headers: {} }
  if (body) {
    opts.body = JSON.stringify(body)
    ;(opts.headers as Record<string, string>)['content-type'] = 'application/json'
  }
  let r: Response
  try { r = await fetch(path, opts) }
  catch { throw new ApiError(0, 'Ошибка сети') }

  if (!r.ok) {
    const text = await r.text()
    let msg = text
    try { msg = JSON.parse(text).error || msg } catch {}
    throw new ApiError(r.status, translateError(msg))
  }
  const ct = r.headers.get('content-type') || ''
  return ct.includes('json') ? r.json() : (r.text() as any)
}

export const api = {
  me: () => req<MeResponse>('GET', '/api/me'),

  listCategories: () => req<Category[]>('GET', '/api/categories'),
  createCategory: (data: Partial<Category>) => req<Category>('POST', '/api/categories', data),
  updateCategory: (id: number, data: Partial<Category>) => req<Category>('PATCH', `/api/categories/${id}`, data),
  deleteCategory: (id: number) => req<{ ok: true }>('DELETE', `/api/categories/${id}`),

  listDomains: (params: { search?: string; category?: number; page?: number; limit?: number; sort?: string; order?: 'asc'|'desc' } = {}) => {
    const q = new URLSearchParams()
    if (params.search) q.set('search', params.search)
    if (params.category) q.set('category', String(params.category))
    if (params.page) q.set('page', String(params.page))
    if (params.limit) q.set('limit', String(params.limit))
    if (params.sort) q.set('sort', params.sort)
    if (params.order) q.set('order', params.order)
    return req<DomainsResponse>('GET', `/api/domains?${q}`)
  },
  createDomain: (data: Partial<Domain>) => req<Domain>('POST', '/api/domains', data),
  bulkCreateDomain: (data: { names: string[]; categoryId: number }) =>
    req<{ added: number; duplicate: number; errors: Array<{ name: string; error: string }> }>('POST', '/api/domains/bulk', data),
  updateDomain: (id: number, data: Partial<Domain>) => req<Domain>('PATCH', `/api/domains/${id}`, data),
  deleteDomain: (id: number) => req<{ ok: true }>('DELETE', `/api/domains/${id}`),

  listIpRanges: (params: { group?: number; search?: string } = {}) => {
    const q = new URLSearchParams()
    if (params.group) q.set('group', String(params.group))
    if (params.search) q.set('search', params.search)
    return req<IpRange[]>('GET', `/api/ip-ranges?${q}`)
  },

  listIpGroups: () => req<IpGroup[]>('GET', '/api/ip-groups'),
  createIpGroup: (data: Partial<IpGroup>) => req<IpGroup>('POST', '/api/ip-groups', data),
  updateIpGroup: (id: number, data: Partial<IpGroup>) => req<IpGroup>('PATCH', `/api/ip-groups/${id}`, data),
  deleteIpGroup: (id: number) => req<{ ok: true }>('DELETE', `/api/ip-groups/${id}`),

  listHappRuleSets: () => req<HappRuleSet[]>('GET', '/api/happ-rule-sets'),
  createHappRuleSet: (data: Partial<HappRuleSet>) => req<HappRuleSet>('POST', '/api/happ-rule-sets', data),
  updateHappRuleSet: (id: number, data: Partial<HappRuleSet>) => req<HappRuleSet>('PATCH', `/api/happ-rule-sets/${id}`, data),
  deleteHappRuleSet: (id: number) => req<{ ok: true }>('DELETE', `/api/happ-rule-sets/${id}`),
  createIpRange: (data: Partial<IpRange>) => req<IpRange>('POST', '/api/ip-ranges', data),
  updateIpRange: (id: number, data: Partial<IpRange>) => req<IpRange>('PATCH', `/api/ip-ranges/${id}`, data),
  deleteIpRange: (id: number) => req<{ ok: true }>('DELETE', `/api/ip-ranges/${id}`),

  createHappTag: (data: Partial<HappTag>) => req<HappTag>('POST', '/api/happ-tags', data),
  deleteHappTag: (id: number) => req<{ ok: true }>('DELETE', `/api/happ-tags/${id}`),

  listTargets: () => req<Target[]>('GET', '/api/targets'),
  getTarget: (id: number) => req<Target>('GET', `/api/targets/${id}`),
  createTarget: (data: Partial<Target>) => req<Target>('POST', '/api/targets', data),
  updateTarget: (id: number, data: Partial<Target> & { categoryIds?: number[] }) =>
    req<Target>('PATCH', `/api/targets/${id}`, data),
  regenTargetToken: (id: number) => req<{ token: string }>('POST', `/api/targets/${id}/regenerate-token`),
  deleteTarget: (id: number) => req<{ ok: true }>('DELETE', `/api/targets/${id}`),

  getSettings: () => req<Record<string, string>>('GET', '/api/settings'),
  updateSetting: (key: string, value: string) => req<{ key: string; value: string }>('PUT', `/api/settings/${key}`, { value }),

  listAudit: (limit = 100) => req<AuditEntry[]>('GET', `/api/audit?limit=${limit}`),

  // Panels
  listPanels: () => req<Panel[]>('GET', '/api/panels'),
  createPanel: (data: any) => req<Panel>('POST', '/api/panels', data),
  updatePanel: (id: number, data: any) => req<Panel>('PATCH', `/api/panels/${id}`, data),
  deletePanel: (id: number) => req<{ ok: true }>('DELETE', `/api/panels/${id}`),
  testPanel: (id: number) => req<{ ok: boolean; status?: number; error?: string; profileTitle?: string }>('POST', `/api/panels/${id}/test`),
  syncPanel: (id: number) => req<{ ok: boolean; updated?: number; error?: string; skipped?: boolean; reason?: string }>('POST', `/api/panels/${id}/sync`),
  createRemnawaveToken: (data: { url: string; username: string; password: string; tokenName?: string }) =>
    req<{ token: string }>('POST', '/api/panels/create-token', data),

  // API tokens (Bearer-доступ к REST API)
  listApiTokens: () => req<ApiToken[]>('GET', '/api/tokens'),
  createApiToken: (data: { name: string; role: ApiToken['role'] }) => req<ApiToken & { token: string }>('POST', '/api/tokens', data),
  updateApiToken: (id: number, data: Partial<Pick<ApiToken, 'name' | 'enabled' | 'role'>>) => req<ApiToken>('PATCH', `/api/tokens/${id}`, data),
  deleteApiToken: (id: number) => req<{ ok: true }>('DELETE', `/api/tokens/${id}`)
}
